import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from '../lib/axios';

export default class ListContentSelector extends Component {
  static propTypes = {
    apiUrl: PropTypes.string.isRequired,
    minCharactersToComplete: PropTypes.number,
    autoCompleteTimeout: PropTypes.number,
    inputParameterName: PropTypes.string.isRequired,
    filteredItemIds: PropTypes.array,
    listType: PropTypes.string.isRequired,
  };

  static defaultProps = {
    apiUrl: '',
    minCharactersToComplete: 3,
    autoCompleteTimeout: 300, // milliseconds
    inputParameterName: '',
    filteredItemIds: [],
    listType: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      textfieldValue: '',
      showPicker: false,
      suggestions: [],
      selectedSuggestionIndex: 0,
      selectedOptions: [],
    };
  }

  // -------------------------------------------------
  // API request and data transformation
  // -------------------------------------------------

  loadSuggestions(query) {
    axios
      .request({
        url: this.props.apiUrl + '&query=' + query,
        method: 'get',
      })
      .then(response => {
        this.setState({
          suggestions: this.transformSuggestions(response.data),
          showPicker: response.data.length > 0,
        });
      });
  }

  handleSubtitle(item) {
    switch (this.props.listType) {
      case 'ProgrammingList':
        return moment(item.start_date)
          .tz(moment.tz.guess())
          .format('dddd, MMM D YYYY');
      case 'CurriculumList':
        return '';
      default:
        return '';
    }
  }

  // Transforms the data from the API
  transformSuggestions(suggestions) {
    const filteredSuggestions = suggestions.map(item => ({
      id: item.id,
      subtitle: this.handleSubtitle(item),
      title: item.name,
    }));

    // Filters the API results that should be ignored
    // by looking at filteredItemIds
    return filteredSuggestions.filter(item => !this.props.filteredItemIds.includes(item.id));
  }

  // -------------------------------------------------
  // Event handling
  // -------------------------------------------------

  // Handles changes to the input itself (usually the user typing in a search term)
  handleChange = e => {
    // Save a reference to the input's current value
    const value = e.target.value;

    // Only start populating at minCharactersToComplete
    // Use a timeout of autoCompleteTimeout to not spam the
    // API server
    if (value.length >= this.props.minCharactersToComplete) {
      clearTimeout(this.autoCompleteTimer);
      this.autoCompleteTimer = setTimeout(
        () => this.loadSuggestions(value),
        this.props.autoCompleteTimeout
      );
    }

    // Change the state so that the text field displays its value
    this.setState({
      textfieldValue: value,
    });
  };

  // Handles arrow-key selection of suggestions
  handleKeyDown = e => {
    if (!this.state.showPicker) return;

    const KEYCODES = {
      RETURN: 13,
      ESC: 27,
      LEFT: 37,
      UP: 38,
      RIGHT: 39,
      DOWN: 40,
    };

    switch (e.keyCode) {
      case KEYCODES.UP:
        e.preventDefault();
        this.shiftSelectedIndex(-1);
        break;

      case KEYCODES.DOWN:
        e.preventDefault();
        this.shiftSelectedIndex(+1);
        break;

      case KEYCODES.ESC:
        e.preventDefault();
        this.clearSuggestions();
        break;

      case KEYCODES.RETURN: {
        e.preventDefault();
        this.selectSuggestion(this.state.selectedSuggestionIndex);
        break;
      }

      // NOOP
      default:
    }
  };

  // Handles mouse selection of suggestions
  handleOnMouseEnter = selectedSuggestionIndex => {
    this.setState({ selectedSuggestionIndex });
  };

  // Handles mouse selection of suggestions
  handleMouseClick = selectedSuggestionIndex => {
    this.selectSuggestion(selectedSuggestionIndex);
  };

  // -------------------------------------------------
  // Selection handling
  // -------------------------------------------------

  // Shifts the currently selected index. Called when the user
  // is making a selection via the keyboard (see the event handling
  // itself above)
  shiftSelectedIndex = delta => {
    const count = this.state.suggestions.length;
    this.setState({
      selectedSuggestionIndex: (count + this.state.selectedSuggestionIndex + delta) % count,
    });
  };

  // Select a specific suggestion from the list. Usually called after
  // the user selects a suggestion via the mouse or keyboard (see the
  // event handling itself above.
  selectSuggestion(selectedSuggestionIndex) {
    const selectedSuggestions = this.state.selectedOptions;

    // Add the selected item's id to the list of items to filter by
    // so that we don't show it when autocompleting a second time
    this.props.filteredItemIds.push(this.state.suggestions[selectedSuggestionIndex].id);

    // Add the selected item to the list of selected suggestions
    selectedSuggestions.push(this.state.suggestions[selectedSuggestionIndex]);

    this.setState({ selectedOptions: selectedSuggestions });
    this.clearSuggestions();
  }

  removeSelection(selectedSuggestionIndex) {
    const selectedSuggestions = this.state.selectedOptions;

    // Grab the id for the element we're removing from the selected Options
    // and remove the entry from the list of filtered IDs (IDs we want to keep
    // out of the suggestion list)
    this.props.filteredItemIds.splice(
      this.props.filteredItemIds.indexOf(selectedSuggestions[selectedSuggestionIndex].id),
      1
    );

    selectedSuggestions.splice(selectedSuggestionIndex, 1);
    this.setState({ selectedOptions: selectedSuggestions });
  }

  // Clears the suggestion list and resets the state. Called after
  // a selection has been made.
  clearSuggestions() {
    this.setState({
      suggestions: [],
      selectedSuggestionIndex: 0,
      textfieldValue: '',
      showPicker: false,
    });
  }

  // -------------------------------------------------
  // Rendering
  // -------------------------------------------------

  // Renders the list of options found via the API
  renderSuggestedItems() {
    return this.state.suggestions.map((item, index) => {
      const itemClassName =
        this.state.selectedSuggestionIndex === index
          ? 'list-content-selector-item list-content-selector-item-selected'
          : 'list-content-selector-item';

      return (
        <li
          key={item.id}
          className={itemClassName}
          onClick={this.handleMouseClick.bind(this, index)}
          onMouseEnter={this.handleOnMouseEnter.bind(this, index)}>
          {item.title}
          <span className="list-content-selector-item-subtitle">{item.subtitle}</span>
        </li>
      );
    });
  }

  renderSubmitButton() {
    return (
      <div className="u-mb-8">
        <input
          type="submit"
          name="commit"
          className="u-mt-2 Button Button--primary Button--small"
          value="Confirm and add content to the list"
        />
      </div>
    );
  }

  // Renders the currently selected items to add to the list
  renderSelectedOptionsTable() {
    return (
      <table className="u-w-full">
        <thead>
          <tr>
            <th colSpan="3">Title</th>
          </tr>
        </thead>
        <tbody>{this.renderSelectedOptions()}</tbody>
      </table>
    );
  }

  renderSelectedOptions() {
    return this.state.selectedOptions.map((item, index) => (
      <tr>
        <td>{item.title}</td>
        <td>{item.subtitle}</td>
        <td className="u-text-right">
          <a onClick={this.removeSelection.bind(this, index)} className="Button Button--red">
            <i className="Icon Icon-delete u-mr-0" />
          </a>
        </td>
        <input value={item.id} name={this.props.inputParameterName} key={index} type="hidden" />
      </tr>
    ));
  }

  // Renders the full component
  render() {
    return (
      <div className="list-content-selector">
        {this.state.selectedOptions.length > 0 && this.renderSelectedOptionsTable()}
        {this.state.selectedOptions.length > 0 && this.renderSubmitButton()}

        <input
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          value={this.state.textfieldValue}
          onKeyDown={this.handleKeyDown}
          onChange={this.handleChange}
          placeholder="Start typing to find content to add to this list..."
        />
        {this.state.showPicker && <ul>{this.renderSuggestedItems()}</ul>}
      </div>
    );
  }
}
